import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  deleteField,
  FieldPath,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';

const COLLECTION_NAME = 'users';

/**
 * Create user document in Firestore
 * @param {string} uid - Firebase Auth user ID
 * @param {Object} userData - User data
 * @returns {Promise<void>}
 */
export const createUserDocument = async (uid, userData) => {
  try {
    console.log('🔵 userService: Getting user document reference...');
    console.log('🔵 userService: db =', db);
    const userRef = doc(db, COLLECTION_NAME, uid);
    console.log('✅ userService: User ref created');

    console.log('🔵 userService: Writing user document to Firestore...');
    await setDoc(userRef, {
      uid,
      email: userData.email,
      displayName: userData.displayName,
      userType: userData.userType, // 'volunteer' | 'org-admin' | 'platform-admin'
      role: userData.role || null, // 'owner' | 'co-admin' | null
      organizationId: userData.organizationId || null,
      organizationName: userData.organizationName || null,
      phoneNumber: userData.phoneNumber || '',
      photoURL: userData.photoURL || '',

      // Volunteer-specific fields
      ...(userData.userType === 'volunteer' && {
        skills: userData.skills || [],
        availability: userData.availability || {},
        totalHours: 0,
      }),

      status: userData.status || 'active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    console.log('✅ userService: User document written successfully');
  } catch (error) {
    console.error('❌ userService error:', error);
    throw new Error('Failed to create user profile: ' + error.message);
  }
};

/**
 * Get user document by ID
 * @param {string} uid - User ID
 * @returns {Promise<Object>} - User data
 */
export const getUserDocument = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, COLLECTION_NAME, uid));

    if (!userDoc.exists()) {
      return null;
    }

    return {
      uid: userDoc.id,
      ...userDoc.data(),
    };
  } catch (error) {
    console.error('Error getting user document:', error);
    throw new Error('Failed to get user profile: ' + error.message);
  }
};

/**
 * Update user document
 * @param {string} uid - User ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export const updateUserDocument = async (uid, updates) => {
  try {
    const userRef = doc(db, COLLECTION_NAME, uid);
    // Keep organizationIds flat array in sync whenever organizations array is updated
    const extra = {};
    if (updates.organizations) {
      extra.organizationIds = updates.organizations.map(o => o.organizationId);
    }
    await updateDoc(userRef, {
      ...updates,
      ...extra,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating user document:', error);
    throw new Error('Failed to update user profile: ' + error.message);
  }
};

/**
 * Get users by organization ID
 * @param {string} organizationId
 * @returns {Promise<Array>} - Array of users in the organization
 */
export const getUsersByOrganization = async (organizationId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('organizationId', '==', organizationId),
      orderBy('displayName', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const users = [];

    querySnapshot.forEach((doc) => {
      users.push({
        uid: doc.id,
        ...doc.data(),
      });
    });

    return users;
  } catch (error) {
    console.error('Error getting users by organization:', error);
    throw new Error('Failed to get organization users: ' + error.message);
  }
};

/**
 * Get volunteers by organization ID
 * @param {string} organizationId
 * @returns {Promise<Array>} - Array of volunteers in the organization
 */
export const getVolunteersByOrganization = async (organizationId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('organizationId', '==', organizationId),
      where('userType', '==', 'volunteer')
    );

    const querySnapshot = await getDocs(q);
    const volunteers = [];

    querySnapshot.forEach((doc) => {
      volunteers.push({
        uid: doc.id,
        ...doc.data(),
      });
    });

    // Sort in memory instead of Firestore to avoid composite index requirement
    volunteers.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));

    return volunteers;
  } catch (error) {
    console.error('Error getting volunteers by organization:', error);
    throw new Error('Failed to get volunteers: ' + error.message);
  }
};

/**
 * Get org admins by organization ID
 * @param {string} organizationId
 * @returns {Promise<Array>} - Array of org admins
 */
export const getOrgAdminsByOrganization = async (organizationId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('organizationId', '==', organizationId),
      where('userType', '==', 'org-admin')
      // Removed: orderBy('displayName', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const admins = [];

    querySnapshot.forEach((doc) => {
      admins.push({
        uid: doc.id,
        ...doc.data(),
      });
    });

    // Sort in memory instead of Firestore to avoid composite index requirement
    admins.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));

    return admins;
  } catch (error) {
    console.error('Error getting org admins by organization:', error);
    throw new Error('Failed to get org admins: ' + error.message);
  }
};

/**
 * Update user's total volunteer hours
 * @param {string} uid - User ID
 * @param {number} additionalHours - Hours to add
 * @returns {Promise<void>}
 */
export const updateVolunteerHours = async (uid, additionalHours) => {
  try {
    const user = await getUserDocument(uid);
    const currentHours = user.totalHours || 0;

    await updateUserDocument(uid, {
      totalHours: currentHours + additionalHours,
    });
  } catch (error) {
    console.error('Error updating volunteer hours:', error);
    throw new Error('Failed to update volunteer hours: ' + error.message);
  }
};

/**
 * Add an organization to the user's organizations array
 * @param {string} uid - User ID
 * @param {string} organizationId - Organization ID to add
 * @param {string} organizationName - Organization name
 * @param {string} status - User's status in the organization (default: 'pending')
 * @returns {Promise<void>}
 */
export const addOrganizationToUser = async (uid, organizationId, organizationName, status = 'pending') => {
  try {
    const userRef = doc(db, COLLECTION_NAME, uid);
    const user = await getDoc(userRef);
    const userData = user.data();

    const updates = {
      organizations: arrayUnion({
        organizationId,
        organizationName,
        status,
        joinedAt: new Date(),
      }),
      updatedAt: serverTimestamp(),
    };

    // If user has no primary org set, also set the primary fields
    if (!userData?.organizationId) {
      updates.organizationId = organizationId;
      updates.organizationName = organizationName;
    }

    await updateDoc(userRef, updates);
  } catch (error) {
    console.error('Error adding organization to user:', error);
    throw new Error('Failed to add organization: ' + error.message);
  }
};

/**
 * Set the user's default organization
 * @param {string} uid - User ID
 * @param {string} organizationId - Organization ID to set as default
 * @param {string} organizationName - Organization name
 * @returns {Promise<void>}
 */
export const setDefaultOrganization = async (uid, organizationId, organizationName) => {
  try {
    await updateDoc(doc(db, COLLECTION_NAME, uid), {
      defaultOrganizationId: organizationId,
      defaultOrganizationName: organizationName,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error setting default organization:', error);
    throw new Error('Failed to set default organization: ' + error.message);
  }
};

/**
 * Promote a volunteer to co-admin of an organization
 * @param {string} uid - User ID to promote
 * @param {string} organizationId - Organization ID
 * @param {string} organizationName - Organization name
 * @returns {Promise<void>}
 */
export const promoteToCoAdmin = async (uid, organizationId, organizationName) => {
  try {
    await updateDoc(doc(db, COLLECTION_NAME, uid), {
      userType: 'org-admin',
      role: 'co-admin',
      organizationId,
      organizationName,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error promoting to co-admin:', error);
    throw new Error('Failed to promote to co-admin: ' + error.message);
  }
};

/**
 * Remove co-admin status, reverting user to volunteer
 * @param {string} uid - User ID
 * @param {string} organizationId - Organization ID
 * @param {string} organizationName - Organization name
 * @returns {Promise<void>}
 */
export const removeCoAdmin = async (uid, organizationId, organizationName) => {
  try {
    await updateDoc(doc(db, COLLECTION_NAME, uid), {
      userType: 'volunteer',
      role: null,
      organizationId,
      organizationName,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error removing co-admin:', error);
    throw new Error('Failed to remove co-admin: ' + error.message);
  }
};

/**
 * Get co-admins (org-admins with role 'co-admin') for an organization
 * @param {string} organizationId
 * @returns {Promise<Array>}
 */
export const getCoAdminsByOrganization = async (organizationId) => {
  try {
    const admins = await getOrgAdminsByOrganization(organizationId);
    return admins.filter((a) => a.role === 'co-admin');
  } catch (error) {
    console.error('Error getting co-admins:', error);
    throw new Error('Failed to get co-admins: ' + error.message);
  }
};

/**
 * Check if user is org admin
 * @param {string} uid - User ID
 * @returns {Promise<boolean>}
 */
export const isOrgAdmin = async (uid) => {
  try {
    const user = await getUserDocument(uid);
    return user && user.userType === 'org-admin';
  } catch (error) {
    console.error('Error checking org admin status:', error);
    return false;
  }
};

const FCM_TOKEN_KEY = 'ss_fcm_token';

/**
 * Save an FCM push notification token for a user device.
 * Rotates the old token out when it changes (best-effort, via localStorage) so
 * stale tokens don't accumulate and cause duplicate notifications on token
 * refresh (e.g. after SW update). This is only a fast path — localStorage on
 * iOS home-screen PWAs isn't reliably persisted across relaunches, so every
 * saved token also gets a per-token timestamp; a scheduled Cloud Function
 * (pruneStaleFcmTokens) sweeps tokens that haven't been refreshed in 30 days
 * so duplicates self-heal even when the localStorage rotation misses.
 */
export const saveFcmToken = async (uid, token) => {
  const userRef = doc(db, COLLECTION_NAME, uid);
  const previousToken = localStorage.getItem(FCM_TOKEN_KEY);
  if (previousToken && previousToken !== token) {
    await updateDoc(
      userRef,
      'fcmTokens', arrayRemove(previousToken),
      new FieldPath('fcmTokenTimestamps', previousToken), deleteField()
    );
  }
  await updateDoc(
    userRef,
    'fcmTokens', arrayUnion(token),
    'pushNotificationsEnabled', true,
    new FieldPath('fcmTokenTimestamps', token), serverTimestamp()
  );
  localStorage.setItem(FCM_TOKEN_KEY, token);
};

/**
 * Clear all FCM tokens and disable push notifications for a user.
 */
export const clearFcmTokens = async (uid) => {
  const userRef = doc(db, COLLECTION_NAME, uid);
  await updateDoc(userRef, {
    fcmTokens: [],
    fcmTokenTimestamps: {},
    pushNotificationsEnabled: false,
  });
  localStorage.removeItem(FCM_TOKEN_KEY);
};

/**
 * Leave an organization: cancels all future registrations for that org,
 * removes it from the user's organizations array, and clears primary org
 * fields if needed. Past registrations are preserved for historical records.
 * @param {string} uid - User ID
 * @param {string} organizationId - Organization to leave
 * @returns {Promise<number>} Number of future registrations cancelled
 */
export const leaveOrganization = async (uid, organizationId) => {
  try {
    const userRef = doc(db, COLLECTION_NAME, uid);
    const userSnap = await getDoc(userRef);
    const userData = userSnap.data();

    // 1. Cancel all future registered slots in this org
    const now = Timestamp.now();
    const regQuery = query(
      collection(db, 'registrations'),
      where('volunteerId', '==', uid),
      where('organizationId', '==', organizationId),
      where('status', '==', 'registered'),
      where('eventDate', '>=', now)
    );
    const regSnap = await getDocs(regQuery);
    await Promise.all(
      regSnap.docs.map((d) =>
        updateDoc(doc(db, 'registrations', d.id), {
          status: 'cancelled',
          cancellationReason: 'Volunteer left the organization',
          updatedAt: serverTimestamp(),
        })
      )
    );

    // 2. Remove org from organizations array
    const currentOrgs = userData.organizations || [];
    const updatedOrgs = currentOrgs.filter((o) => o.organizationId !== organizationId);

    // 3. Build updates — pick a replacement primary org if needed
    const updates = {
      organizations: updatedOrgs,
      updatedAt: serverTimestamp(),
    };

    if (userData.organizationId === organizationId) {
      const nextOrg = updatedOrgs[0] || null;
      updates.organizationId = nextOrg?.organizationId || null;
      updates.organizationName = nextOrg?.organizationName || null;
      updates.status = nextOrg?.status || null;
    }

    if (userData.defaultOrganizationId === organizationId) {
      updates.defaultOrganizationId = updatedOrgs[0]?.organizationId || null;
    }

    await updateDoc(userRef, updates);
    return regSnap.size;
  } catch (error) {
    console.error('Error leaving organization:', error);
    throw new Error('Failed to leave organization: ' + error.message);
  }
};

/**
 * Get all users across all organizations (platform admin only)
 * @returns {Promise<Array>}
 */
export const getAllUsers = async () => {
  try {
    const snapshot = await getDocs(query(collection(db, COLLECTION_NAME), orderBy('displayName')));
    return snapshot.docs.map(d => ({ uid: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error getting all users:', error);
    throw new Error('Failed to get users: ' + error.message);
  }
};

/**
 * Check if user is platform admin
 * @param {string} uid - User ID
 * @returns {Promise<boolean>}
 */
export const isPlatformAdmin = async (uid) => {
  try {
    const user = await getUserDocument(uid);
    return user && user.userType === 'platform-admin';
  } catch (error) {
    console.error('Error checking platform admin status:', error);
    return false;
  }
};
